// tickerSimulator.js
import CoreBase from '../../../../../services/engines/CoreBase';
import cardDataEngine from '../../../../../Widgets/DataPainterWidget/Core/CardDataEngine';
import cardPainterManager from '../../../../../Widgets/DataPainterWidget/Core/CardPainterManager';
import { CMD_TICKER_REGISTRY_KEY, META_TYPE, META_VER } from './tickerCommandRegisteryType';

const MIN_INTERVAL_MS = 200;
const MAX_INTERVAL_MS = 1200;
const TICKERS_KEY = '__jernyTickers';

/**
 * Mock ticker stream generator — dev/test only.
 * Pushes SimpleExchangeTicker-shaped envelopes through the real cardDataListener chain.
 */
class TickerSimulator extends CoreBase {
    _name = 'TickerSimulator';

    _getTickers = () => {
        if (!window[TICKERS_KEY]) window[TICKERS_KEY] = {};
        return window[TICKERS_KEY];
    };

    _randomInterval = () => MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
    _randomPrice = (min, max) => min + Math.random() * (max - min);

    _buildEnvelope = (target, eMarket, min, max) => {
        const mid = this._randomPrice(min, max);
        const spread = mid * 0.0015;
        const ticker = {
            eMarket,
            eid: 100,
            sid: 0,
            qid: 1000,
            ask: Number((mid + spread / 2).toFixed(4)),
            bid: Number((mid - spread / 2).toFixed(4)),
            ems: Date.now(),
        };
        return { meta: { type: META_TYPE, ver: META_VER, target }, data: JSON.stringify(ticker) };
    };

    _tick = (target) => {
        const state = this._getTickers()[target];
        if (!state?.running) return;

        try {
            cardDataEngine.onMessage(this._buildEnvelope(target, state.eMarket, state.min, state.max));
        } catch (err) {
            this.logError('_tick', `envelope push failed for ${target}: ${err.message}`);
            state.running = false;
            return;
        }

        state.timeoutId = setTimeout(() => this._tick(target), this._randomInterval());
    };

    /**
     * Starts (or re-ranges) the mock stream for a market.
     * @param {string} target - lowercase market key, e.g. 'near-euro'
     * @param {string} eMarket - uppercase market label, e.g. 'NEAR-EURO'
     * @param {number} low
     * @param {number} high
     */
    start = (target, eMarket, low, high) => {
        const tickers = this._getTickers();
        const existing = tickers[target];

        if (existing?.running) {
            existing.min = low;
            existing.max = high;
            existing.eMarket = eMarket;
            return;
        }

        try {
            tickers[target] = { running: true, min: low, max: high, eMarket, timeoutId: null };
            cardPainterManager.openCard(CMD_TICKER_REGISTRY_KEY, { type: META_TYPE, ver: META_VER, target });
        } catch (err) {
            this.logError('start', `openCard failed for ${target}: ${err.message}`);
            delete tickers[target];
            return;
        }

        // First open of a card mounts it async — widget subscribes to cardDataListener in
        // componentDidMount, which hasn't run yet in this same tick.
        setTimeout(() => this._tick(target), 0);
    };

    /**
     * Stops the mock stream. No target = stops all running streams.
     * @param {string} [target]
     */
    stop = (target) => {
        const tickers = this._getTickers();

        if (!target) {
            Object.values(tickers).forEach((state) => {
                state.running = false;
                if (state.timeoutId) clearTimeout(state.timeoutId);
            });
            return;
        }

        const state = tickers[target];
        if (!state) return;
        state.running = false;
        if (state.timeoutId) clearTimeout(state.timeoutId);
    };
}

const tickerSimulator = new TickerSimulator();
export default tickerSimulator;
